import { useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../Button/Button'
import { cx } from '../utils/cx'
import { useCopy } from '../utils/useCopy'
import './DigitalKey.css'

export interface CodeViewerProps {
  code: string
  /** Por defecto "Código del teclado" */
  label?: ReactNode
  /** "Válido del 3 oct 16:00 al 6 oct 11:00. Solo lo ven las personas registradas." */
  validity?: ReactNode
  /** Fuera de la llave (AccesosDetalle.dc.html `code`): sin la línea superior */
  standalone?: boolean
  className?: string
}

/**
 * Visor de código de teclado (bundle B4 §4c): oculto con puntos, Mostrar/Ocultar y Copiar de 48 px, cifras
 * tabulares. Es el mismo visor que la llave digital muestra dentro; se exporta para la pantalla del código.
 */
export function CodeViewer({ code, label, validity, standalone, className }: CodeViewerProps) {
  const { t } = useTranslation()
  const [show, setShow] = useState(false)
  const { copied, copy } = useCopy()
  return (
    <div className={cx('hp-key__code', standalone && 'hp-key__code--standalone', className)}>
      <div>
        <div className="hp-key__code-label">{label ?? t('v2.ds.digitalKey.codigo')}</div>
        <div className="hp-key__code-value tnum" aria-live="polite">
          {show ? code : '•'.repeat(code.length)}
          <span className="visually-hidden">{show ? '' : t('v2.ds.digitalKey.codigoOculto')}</span>
        </div>
        {validity && <div className="hp-key__code-validity">{validity}</div>}
      </div>
      <div className="hp-key__code-actions">
        <Button variant="secondary" fullWidth={false} icon={show ? 'eye-off' : 'eye'} onClick={() => setShow((s) => !s)}>
          {show ? t('v2.ds.digitalKey.ocultar') : t('v2.ds.digitalKey.mostrar')}
        </Button>
        <Button variant="secondary" fullWidth={false} icon={copied ? 'check' : 'copy'} onClick={() => void copy(code)}>
          {copied ? t('v2.ds.digitalKey.copiado') : t('v2.ds.digitalKey.copiar')}
        </Button>
      </div>
    </div>
  )
}
